import { useCallback, useEffect, useState } from "react";

import { useT } from "../../../shared/i18n";
import { Badge, Banner, Button, Skeleton } from "../../../shared/ui";
import { AdminListRow } from "../AdminListRow";
import { ErrorRetry } from "../ErrorRetry";
import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import {
  formatDuration,
  formatTime,
  outcomeTone,
  toListQuery,
  type LogFilters,
} from "./logFormat";
import { TraceFilters } from "./TraceFilters";
import type { TraceApi, TraceSummary } from "./traceApi";

type Status = "loading" | "ready" | "failed" | "denied" | "invalid";

/** Calls (one row per correlation id) matching the filters, newest first. */
export function TraceList({
  api,
  filters,
  onFiltersChange,
  onOpen,
  selectedId = null,
  hrefFor,
  hideUserFilter = false,
}: {
  api: TraceApi;
  filters: LogFilters;
  onFiltersChange: (next: LogFilters) => void;
  onOpen: (correlationId: string) => void;
  selectedId?: string | null;
  /** Real URL of a call; omitted when the list opens calls in local state. */
  hrefFor?: (correlationId: string) => string;
  hideUserFilter?: boolean;
}) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [items, setItems] = useState<TraceSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [status, setStatus] = useState<Status>("loading");

  const fetchPage = useCallback(
    (after: string | null) => {
      api
        .list({ ...toListQuery(filters), cursor: after })
        .then((page) => {
          setItems((current) =>
            after === null ? page.items : [...current, ...page.items],
          );
          setCursor(page.next_cursor);
          setUnavailable(page.degraded === "unavailable");
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (isSessionExpired(error)) reportExpired();
          if (
            error instanceof OperatorRefused &&
            error.code === "invalid_identifier"
          ) {
            setStatus("invalid");
            return;
          }
          setStatus(error instanceof OperatorDenied ? "denied" : "failed");
        });
    },
    [api, filters, reportExpired],
  );

  const load = (after: string | null) => {
    setStatus("loading");
    fetchPage(after);
  };

  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  if (status === "denied") {
    return <PermissionDenied message={t("admin.denied.logs")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <TraceFilters
        filters={filters}
        onApply={onFiltersChange}
        hideUser={hideUserFilter}
      />
      {status === "invalid" && (
        <p role="alert" className="text-footnote text-error">
          {t("admin.logs.invalid")}
        </p>
      )}
      {status === "failed" && (
        <ErrorRetry
          message={t("admin.logs.failed")}
          onRetry={() => {
            load(null);
          }}
        />
      )}
      {unavailable && status === "ready" && (
        <Banner tone="warning">{t("admin.logs.unavailable")}</Banner>
      )}
      {status === "loading" && items.length === 0 && (
        <div aria-busy="true">
          <Skeleton height={48} />
          <span className="sr-only">{t("admin.logs.loading")}</span>
        </div>
      )}
      {status === "ready" && !unavailable && items.length === 0 && (
        <p className="text-body text-fg-muted">{t("admin.logs.empty")}</p>
      )}
      {status === "ready" && !unavailable && items.length > 0 && (
        <ul
          aria-label={t("admin.logs.list.caption")}
          className="divide-y divide-line-subtle overflow-hidden rounded-xl bg-raised shadow-card"
        >
          {items.map((row) => (
            <li key={row.correlation_id}>
              <AdminListRow
                href={hrefFor?.(row.correlation_id)}
                selected={row.correlation_id === selectedId}
                onSelect={() => {
                  onOpen(row.correlation_id);
                }}
              >
                <span className="flex min-w-0 items-center justify-between gap-2">
                  <span className="truncate text-callout font-semibold text-fg">
                    {row.entry_event}
                  </span>
                  <Badge tone={outcomeTone(row.last_outcome, row.error_count)}>
                    {row.last_outcome ?? "-"}
                  </Badge>
                </span>
                <span className="truncate text-caption text-fg-muted">
                  {`${formatTime(row.time)} · ${row.service} · ${formatDuration(row.duration_ms)} · ${String(row.error_count)} ${t("admin.logs.col.errors")}`}
                </span>
              </AdminListRow>
            </li>
          ))}
        </ul>
      )}
      {cursor !== null && status === "ready" && (
        <Button
          variant="secondary"
          onClick={() => {
            load(cursor);
          }}
        >
          {t("admin.logs.more")}
        </Button>
      )}
    </div>
  );
}
